import api, { handleApiResponse } from './api';
import { ApiResponse } from '../types';
import { PurchaseOrderRequest } from '../components/PurchaseOrders/PurchaseOrderFormData';

// Response type (ajustar según lo que devuelva el backend)
export interface PurchaseOrderResponse {
  id: number;
  supplierId: number;
  orderDate: string;
  accountingType: string;
  ivaCode: string | null;
  currency: string;
  costCenterStrategy: string;
  totalAmount?: number;
  // Agregar más campos según la respuesta del backend
}

// Response type para listado de órdenes
export interface PurchaseOrderListResponse {
  id: number;
  orderNumber: string;
  supplierName: string;
  supplierCuit: string;
  orderDate: string;
  accountingType: string;
  currency: string;
  subtotal: number;
  ivaTotal: number;
  total: number;
  status: string;
  documentsCount: number;
}

// Response type para centro de costo global
export interface GlobalCostCenterResponse {
  costCenterType: 'PROJECT' | 'GENERAL';
  projectId: number | null;
  generalCostCenterId: number | null;
  workCategoryId: number;
}

// Response type para asignaciones de items
export interface PurchaseOrderItemAllocationResponse {
  costCenterType: 'PROJECT' | 'GENERAL';
  projectId: number | null;
  generalCostCenterId: number | null;
  workCategoryId: number;
  allocatedQuantity: number;
}

// Response type para items de la orden
export interface PurchaseOrderItemResponse {
  id: number;
  description: string;
  unit: string;
  unitPrice: number;
  quantity: number;
  lineSubtotal: number;
  lineIva: number;
  lineTotal: number;
  allocations: PurchaseOrderItemAllocationResponse[];
}

// Response type para detalle completo de orden
export interface PurchaseOrderDetailResponse {
  id: number;
  orderNumber: string;
  supplierId: number;
  orderDate: string;
  accountingType: string;
  ivaCode: string | null;
  currency: string;
  costCenterStrategy: 'GLOBAL' | 'PER_LINE' | 'UNASSIGNED';
  globalCostCenter: GlobalCostCenterResponse | null;
  subtotal: number;
  ivaTotal: number;
  total: number;
  status: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  items: PurchaseOrderItemResponse[];
}

// Request type para actualizar estado
export interface UpdateStatusRequest {
  status: 'BORRADOR' | 'ENVIADO' | 'RECIBIDO' | 'CANCELADO';
}

export const purchaseOrderService = {
  // ==================== CREAR ORDEN DE COMPRA ====================

  /**
   * Crear nueva orden de compra
   * @param request - Datos de la orden de compra a crear
   * @returns Promise con la orden de compra creada
   */
  async createPurchaseOrder(
    request: PurchaseOrderRequest,
  ): Promise<PurchaseOrderResponse> {
    const response = await api.post<ApiResponse<PurchaseOrderResponse>>(
      'purchase-orders',
      request,
    );
    return handleApiResponse(response);
  },

  // ==================== OBTENER ÓRDENES DE COMPRA ====================

  /**
   * Obtener todas las órdenes de compra
   * @returns Promise con la lista de órdenes de compra
   */
  async getAllPurchaseOrders(): Promise<PurchaseOrderListResponse[]> {
    const response =
      await api.get<ApiResponse<PurchaseOrderListResponse[]>>(
        'purchase-orders',
      );
    return handleApiResponse(response);
  },

  // ==================== OBTENER ORDEN POR ID ====================

  /**
   * Obtener orden de compra por ID
   * @param id - ID de la orden de compra
   * @returns Promise con el detalle completo de la orden
   */
  async getPurchaseOrderById(id: number): Promise<PurchaseOrderDetailResponse> {
    const response = await api.get<ApiResponse<PurchaseOrderDetailResponse>>(
      `purchase-orders/${id}`,
    );
    return handleApiResponse(response);
  },

  // ==================== ACTUALIZAR ORDEN DE COMPRA ====================

  /**
   * Actualizar orden de compra existente
   * @param id - ID de la orden de compra a actualizar
   * @param request - Datos actualizados de la orden de compra
   * @returns Promise con la orden de compra actualizada
   */
  async updatePurchaseOrder(
    id: number,
    request: PurchaseOrderRequest,
  ): Promise<PurchaseOrderResponse> {
    const response = await api.put<ApiResponse<PurchaseOrderResponse>>(
      `purchase-orders/${id}`,
      request,
    );
    return handleApiResponse(response);
  },

  // ==================== ACTUALIZAR ESTADO DE ORDEN ====================

  /**
   * Actualizar estado de orden de compra
   * @param id - ID de la orden de compra
   * @param status - Nuevo estado (BORRADOR, ENVIADO, RECIBIDO, CANCELADO)
   * @returns Promise con la orden de compra actualizada
   */
  async updatePurchaseOrderStatus(
    id: number,
    status: 'BORRADOR' | 'ENVIADO' | 'RECIBIDO' | 'CANCELADO',
  ): Promise<PurchaseOrderResponse> {
    const response = await api.patch<ApiResponse<PurchaseOrderResponse>>(
      `purchase-orders/${id}/status`,
      { status },
    );
    return handleApiResponse(response);
  },

  // ==================== ELIMINAR ORDEN DE COMPRA (Solo borrador) ====================

  /**
   * Actualizar estado de orden de compra
   * @param id - ID de la orden de compra
   * @param status - Nuevo estado (BORRADOR, ENVIADO, RECIBIDO, CANCELADO)
   * @returns Promise con la orden de compra actualizada
   */
  async deletePurchaseOrder(id: number): Promise<PurchaseOrderResponse> {
    const response = await api.delete<ApiResponse<PurchaseOrderResponse>>(
      `purchase-orders/${id}`,
    );
    return handleApiResponse(response);
  },

  // ==================== CONTEO DE DOCUMENTOS ====================

  /**
   * Obtener conteo de documentos (facturas y remitos) de una orden
   * @param id - ID de la orden de compra
   * @returns Promise con mapa { FACTURA: n, REMITO: n }
   */
  async getDocumentCounts(id: number): Promise<Record<string, number>> {
    const response = await api.get<ApiResponse<Record<string, number>>>(
      `purchase-orders/${id}/document-counts`,
    );
    return handleApiResponse(response);
  },

  // ==================== NOTIFICAR CANCELACIÓN AL PROVEEDOR ====================

  /**
   * Envía un email de cancelación al proveedor de la orden
   * @param id - ID de la orden de compra cancelada
   */
  async notifyCancellation(id: number): Promise<void> {
    const response = await api.post<ApiResponse<void>>(
      `purchase-orders/${id}/notify-cancellation`,
    );
    handleApiResponse(response);
  },
};
